import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type PremiumPlan = 'PLAYER' | 'CLUB' | 'BUSINESS';
export type BillingPeriod = 'MONTHLY' | 'YEARLY';

export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED',
  TRIAL = 'TRIAL',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

export interface SubscriptionResponse {
  premiumPlan: PremiumPlan;
  billingPeriod: BillingPeriod;
  status: SubscriptionStatus;
  startedAt: Date;
  currentPeriodEndAt: Date;
  // When set and in the future, the subscription is in its free-trial window (first charge then).
  trialEndAt?: Date;
  canceledAt?: Date;
  autoRenew: boolean;
  hasAccess: boolean; // authoritative premium access flag
  /** Who bills it. A store plan is cancelled or changed in that store only. Absent = Stripe. */
  provider?: 'STRIPE' | 'APP_STORE' | 'GOOGLE_PLAY';
}

/** The store's own subscription settings: the only place a plan bought there is cancelled or changed. */
export const STORE_SUBSCRIPTIONS_URL: Record<'APP_STORE' | 'GOOGLE_PLAY', string> = {
  APP_STORE: 'https://apps.apple.com/account/subscriptions',
  GOOGLE_PLAY: 'https://play.google.com/store/account/subscriptions?package=com.ludoya.app',
};

/** The store billing [subscription], when it was bought in the App Store or Google Play. */
export function storeProviderOf(subscription: SubscriptionResponse | null | undefined): 'APP_STORE' | 'GOOGLE_PLAY' | undefined {
  const provider = subscription?.provider;
  return provider === 'APP_STORE' || provider === 'GOOGLE_PLAY' ? provider : undefined;
}

export interface SubscriptionsResponse {
  subscriptions: SubscriptionResponse[];
}

/** One invoice the account has paid, as Stripe has it; its links are fresh and never kept. */
export interface InvoiceItem {
  id: string;
  number?: string | null;
  paidAt: string;
  total: number;
  tax: number;
  currency: string;
  url?: string | null;
  pdfUrl?: string | null;
}

export interface InvoicesResponse {
  invoices: InvoiceItem[];
}

// Requests per spec
export interface CreateSubscriptionRequest {
  premiumPlan: PremiumPlan;
  billingPeriod: BillingPeriod;
  startTrial: boolean; // must be true; server returns 400 if false
}

export interface UpdateAutoRenewRequest {
  autoRenew: boolean;
}

/** A group account the current user administers — a valid target for subscription operations. */
export interface AdminnedGroup {
  id: string;
  username: string;
  name: string;
  avatarUrl?: string;
  groupType?: string;
  premiumPlan?: PremiumPlan | null;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  constructor(private http: HttpClient) {}

  /** The optional userId targets a group account the caller administers (defaults to the caller). */
  private targetParams(userId?: string): { params?: { userId: string } } {
    return userId ? { params: { userId } } : {};
  }

  /** GET /groups/adminned — Group accounts the current user administers */
  getAdminnedGroups(): Observable<AdminnedGroup[]> {
    return this.http.get<AdminnedGroup[]>(`${environment.apiUrl}/groups/adminned`);
  }

  /** GET /subscriptions — Returns the account's active subscription or 404 if none */
  getActiveSubscription(userId?: string): Observable<SubscriptionResponse> {
    return this.http.get<SubscriptionResponse>(`${environment.apiUrl}/subscriptions`, this.targetParams(userId));
  }

  /** GET /subscriptions/history — Returns all the account's subscriptions over time */
  getSubscriptionsHistory(userId?: string): Observable<SubscriptionsResponse> {
    return this.http.get<SubscriptionsResponse>(`${environment.apiUrl}/subscriptions/history`, this.targetParams(userId));
  }

  /** POST /subscriptions — Create a subscription (trial-only) */
  createTrialSubscription(request: CreateSubscriptionRequest): Observable<SubscriptionResponse> {
    return this.http.post<SubscriptionResponse>(`${environment.apiUrl}/subscriptions`, request);
  }

  /** POST /subscriptions/cancel — Cancel the account's active subscription */
  cancelActiveSubscription(userId?: string): Observable<SubscriptionResponse> {
    return this.http.post<SubscriptionResponse>(`${environment.apiUrl}/subscriptions/cancel`, {}, this.targetParams(userId));
  }

  /** POST /subscriptions/change-plan — In-place plan/period change (no trial; upgrades wanting one go through checkout) */
  changePlan(premiumPlan: PremiumPlan, billingPeriod: BillingPeriod, userId?: string): Observable<SubscriptionResponse> {
    return this.http.post<SubscriptionResponse>(`${environment.apiUrl}/subscriptions/change-plan`, { premiumPlan, billingPeriod }, this.targetParams(userId));
  }

  /** POST /subscriptions/auto-renew — Update auto-renew flag */
  updateAutoRenew(autoRenew: boolean | UpdateAutoRenewRequest, userId?: string): Observable<SubscriptionResponse> {
    const body: UpdateAutoRenewRequest = typeof autoRenew === 'boolean' ? { autoRenew } : autoRenew;
    return this.http.post<SubscriptionResponse>(`${environment.apiUrl}/subscriptions/auto-renew`, body, this.targetParams(userId));
  }

  /** Stripe's page to edit the name, address and tax ID the account's next invoices carry. */
  getBillingDetailsUrl(userId?: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${environment.apiUrl}/subscriptions/billing-details`, {}, this.targetParams(userId));
  }

  /** Every invoice the account has paid, newest first, with links fresh from Stripe. */
  getInvoices(userId?: string): Observable<InvoicesResponse> {
    return this.http.get<InvoicesResponse>(`${environment.apiUrl}/subscriptions/invoices`, this.targetParams(userId));
  }
}
